import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';

import { map, Observable, take } from 'rxjs';

import { TokenSyncService } from '@stylehaven/components';
import { BaseBootstrapSandboxService } from '../sandbox/base-bootstrap.sandbox.service';

@Injectable({ providedIn: 'root' })
export abstract class BaseAuthGuard {
    protected isAuthenticated$: Observable<boolean>;
    protected environment: { offline: boolean };

    protected abstract readonly sandbox: BaseBootstrapSandboxService;
    private readonly router: Router = inject(Router);
    private readonly location: Location = inject(Location);
    private readonly tokenSyncService: TokenSyncService = inject(TokenSyncService);

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const tokens = this.tokenSyncService.tokens;

        if (tokens) {
            return true;
        }

        if (this.environment.offline) {
            // In offline mode, we skip authentication checks and allow access to all routes
            return true;
        }

        this.sandbox.clearStore();

        const loginUrlTree: UrlTree = this.router.parseUrl('/login');
        loginUrlTree.queryParams = {
            entryNavigationUrl: state.url
        };

        return loginUrlTree;
    }

    canMatch(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
        if (this.environment.offline) {
            // In offline mode, we skip authentication checks and allow access to all routes
            return true;
        }

        return this.isAuthenticated$.pipe(
            take(1),
            map((isLoggedIn: boolean) => {
                if (!isLoggedIn) {
                    this.router.navigate(['/login'], {
                        queryParams: {
                            entryNavigationUrl: this.location.path().slice(1)
                        }
                    });
                }
                return isLoggedIn;
            })
        );
    }
}
